import {
  Box,
  Card,
  CardContent,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputAdornment,
  List,
  ListItemButton,
  ListItemText,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { ArrowLeft, Film, Plus, Search } from "lucide-react";
import { useEffect, useState } from "react";
import { Spinner } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import useFetch from "../../global/hooks/useFetch";
import Button from "../../common/components/Button";
import PageHeader from "../../common/components/PageHeader";
import { toast } from "../../common/components/Toast";
import { useAssets, useLinkEpisodes } from "../api/assets";

function SeriesBuilder() {
  const { t } = useTranslation("assets");
  const navigate = useNavigate();

  // Series list (series + standalone that can become series)
  const [seriesSearch, setSeriesSearch] = useState("");
  const {
    assets: seriesAssets,
    createAsset,
    creating,
    refetch: refetchSeries,
  } = useAssets(1, 200, seriesSearch, "");

  // Selected series
  const [selectedSeries, setSelectedSeries] = useState(null);

  // Linked episodes for selected series
  const {
    data: linkedData,
    loading: linkedLoading,
    refetch: refetchLinked,
  } = useFetch(
    selectedSeries
      ? `/assets/?parent_asset_id=${selectedSeries.id}&show_episodes=true&page_size=100`
      : null,
  );
  const linkedEpisodes = linkedData?.results || [];

  // Available standalones to link
  const [episodeSearch, setEpisodeSearch] = useState("");
  const { assets: standaloneAssets, refetch: refetchStandalones } = useAssets(
    1,
    200,
    episodeSearch,
    "standalone",
  );

  // Selection state
  const [selectedIds, setSelectedIds] = useState([]);
  const { linkEpisodes, linking } = useLinkEpisodes();

  // Create series dialog
  const [createOpen, setCreateOpen] = useState(false);
  const [newTitle, setNewTitle] = useState("");

  // Filter series list to show only series + standalones (not episodes)
  const seriesList = seriesAssets.filter(
    (a) => a.asset_type === "series" || a.asset_type === "standalone",
  );

  // Available episodes: standalones minus the selected series itself
  const availableEpisodes = standaloneAssets.filter(
    (a) => a.id !== selectedSeries?.id,
  );

  const toggleSelect = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };

  const toggleSelectAll = () => {
    if (selectedIds.length === availableEpisodes.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(availableEpisodes.map((a) => a.id));
    }
  };

  const handleLink = async () => {
    if (!selectedSeries || selectedIds.length === 0) return;
    try {
      await linkEpisodes(selectedSeries.id, selectedIds);
      toast.success(
        t("seriesBuilder.linkSuccess", { count: selectedIds.length }),
      );
      setSelectedIds([]);
      refetchLinked();
      refetchStandalones();
      refetchSeries();
    } catch (error) {
      toast.error(error.message || "Failed to link episodes");
    }
  };

  const handleCreateSeries = async () => {
    if (!newTitle.trim()) return;
    try {
      const created = await createAsset({
        title: newTitle.trim(),
        asset_type: "series",
      });
      toast.success(t("seriesBuilder.createSuccess"));
      setCreateOpen(false);
      setNewTitle("");
      setSelectedSeries(created);
    } catch (error) {
      toast.error(error.message || "Failed to create series");
    }
  };

  // Clear selection when series changes
  useEffect(() => {
    setSelectedIds([]);
  }, [selectedSeries?.id]);

  return (
    <>
      <PageHeader
        title={t("seriesBuilder.title")}
        description={t("seriesBuilder.description")}
        appendActions={
          <Button
            variant="outlined"
            startIcon={<ArrowLeft size={18} />}
            onClick={() => navigate("/assets")}
          >
            {t("title")}
          </Button>
        }
      />

      <Box sx={{ display: "flex", gap: 3, minHeight: "70vh" }}>
        {/* Left Panel — Series List */}
        <Card
          variant="outlined"
          sx={{ width: 320, flexShrink: 0, overflow: "auto" }}
        >
          <CardContent sx={{ p: 2 }}>
            <Button
              variant="contained"
              size="small"
              startIcon={<Plus size={16} />}
              onClick={() => setCreateOpen(true)}
              fullWidth
              sx={{ mb: 2 }}
            >
              {t("seriesBuilder.createSeries")}
            </Button>

            <TextField
              size="small"
              fullWidth
              placeholder={t("seriesBuilder.searchSeries")}
              value={seriesSearch}
              onChange={(e) => setSeriesSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search size={16} />
                  </InputAdornment>
                ),
              }}
              sx={{ mb: 1 }}
            />

            <List dense disablePadding>
              {seriesList.map((asset) => (
                <ListItemButton
                  key={asset.id}
                  selected={selectedSeries?.id === asset.id}
                  onClick={() => setSelectedSeries(asset)}
                  sx={{ borderRadius: 1, mb: 0.5 }}
                >
                  <ListItemText
                    primary={asset.title}
                    secondary={
                      asset.asset_type === "series" ? (
                        <Box
                          component="span"
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.5,
                          }}
                        >
                          <Film size={12} />
                          {asset.episode_count || 0} {t("episodes")}
                        </Box>
                      ) : (
                        <Chip
                          label={t("standalone")}
                          size="small"
                          variant="outlined"
                          sx={{ height: 18, fontSize: "0.65rem" }}
                        />
                      )
                    }
                    primaryTypographyProps={{
                      variant: "bodySm",
                      fontWeight: 500,
                      noWrap: true,
                    }}
                  />
                </ListItemButton>
              ))}
            </List>
          </CardContent>
        </Card>

        {/* Right Panel — Series Detail */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          {!selectedSeries ? (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                height: "100%",
              }}
            >
              <Typography color="text.secondary">
                {t("seriesBuilder.selectSeries")}
              </Typography>
            </Box>
          ) : (
            <>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                {selectedSeries.title}
              </Typography>

              {/* Linked Episodes */}
              <Card variant="outlined" sx={{ mb: 3 }}>
                <CardContent>
                  <Typography
                    variant="h6"
                    sx={{
                      textTransform: "uppercase",
                      fontWeight: 500,
                      fontSize: "0.875rem",
                      color: "text.secondary",
                      mb: 2,
                    }}
                  >
                    {t("seriesBuilder.linkedEpisodes")}
                  </Typography>

                  {linkedLoading ? (
                    <Spinner animation="border" size="sm" />
                  ) : linkedEpisodes.length === 0 ? (
                    <Typography variant="bodySm" color="text.secondary">
                      {t("seriesBuilder.noLinkedEpisodes")}
                    </Typography>
                  ) : (
                    <Table size="small">
                      <TableBody>
                        {linkedEpisodes.map((ep) => (
                          <TableRow key={ep.id}>
                            <TableCell sx={{ fontWeight: 500 }}>
                              {ep.title}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </CardContent>
              </Card>

              {/* Available Episodes */}
              <Card variant="outlined">
                <CardContent>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      mb: 2,
                    }}
                  >
                    <Typography
                      variant="h6"
                      sx={{
                        textTransform: "uppercase",
                        fontWeight: 500,
                        fontSize: "0.875rem",
                        color: "text.secondary",
                      }}
                    >
                      {t("seriesBuilder.availableEpisodes")}
                    </Typography>
                    {selectedIds.length > 0 && (
                      <Button
                        variant="contained"
                        size="small"
                        onClick={handleLink}
                        disabled={linking}
                      >
                        {t("seriesBuilder.linkSelected", {
                          count: selectedIds.length,
                        })}
                      </Button>
                    )}
                  </Box>

                  <TextField
                    size="small"
                    fullWidth
                    placeholder={t("seriesBuilder.searchEpisodes")}
                    value={episodeSearch}
                    onChange={(e) => setEpisodeSearch(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Search size={16} />
                        </InputAdornment>
                      ),
                    }}
                    sx={{ mb: 2 }}
                  />

                  {availableEpisodes.length === 0 ? (
                    <Typography variant="bodySm" color="text.secondary">
                      {t("seriesBuilder.noAvailableEpisodes")}
                    </Typography>
                  ) : (
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell padding="checkbox">
                            <Checkbox
                              size="small"
                              checked={
                                selectedIds.length ===
                                  availableEpisodes.length &&
                                availableEpisodes.length > 0
                              }
                              indeterminate={
                                selectedIds.length > 0 &&
                                selectedIds.length < availableEpisodes.length
                              }
                              onChange={toggleSelectAll}
                            />
                          </TableCell>
                          <TableCell>{t("editModal.titleLabel")}</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {availableEpisodes.map((ep) => (
                          <TableRow
                            key={ep.id}
                            hover
                            onClick={() => toggleSelect(ep.id)}
                            sx={{ cursor: "pointer" }}
                          >
                            <TableCell padding="checkbox">
                              <Checkbox
                                size="small"
                                checked={selectedIds.includes(ep.id)}
                              />
                            </TableCell>
                            <TableCell>{ep.title}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </CardContent>
              </Card>
            </>
          )}
        </Box>
      </Box>

      {/* Create Series Dialog */}
      <Dialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>{t("seriesBuilder.createSeries")}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            label={t("seriesBuilder.createSeriesTitle")}
            placeholder={t("seriesBuilder.createSeriesPlaceholder")}
            value={newTitle}
            onChange={(e) => setNewTitle(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleCreateSeries()}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button variant="secondary" onClick={() => setCreateOpen(false)}>
            {t("editModal.cancel")}
          </Button>
          <Button
            variant="contained"
            onClick={handleCreateSeries}
            disabled={creating || !newTitle.trim()}
          >
            {t("seriesBuilder.create")}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default SeriesBuilder;
